/* ==========================================================================
   a11y.js — keyboard accessibility for this theme
   --------------------------------------------------------------------------
   Plain JavaScript. No Liquid. Shopify serves this file as-is.

   INSTALL
     1. assets/a11y.js   <- this file
     2. In layout/theme.liquid, before </body>:
          <script src="{{ 'a11y.js' | asset_url }}" defer></script>

   EMERGENCY OFF SWITCH
     Add ?a11y=off to any URL and this script does nothing on that page.

   KEYS — EVERYWHERE
     Tab / Shift+Tab   between items; a CLOSED submenu is skipped
     Enter             follow the link / press the button
     Escape            close the open submenu or the cart drawer
     F2 / Shift+F2     cycle through the page's working areas
     F3                open the cart and step into it; press again to close
                       and return focus to where you were

   KEYS — MAIN MENU
     ArrowLeft/Right   between ALL top-level items (flipped for RTL), also
                       from inside an open submenu
     ArrowDown         open a submenu and step into it
     ArrowUp/Down      walk inside an open submenu
     Home / End        first / last item in the submenu

   KEYS — PRODUCT PAGE
     F4                straight to the buy button
     ArrowUp / Down    change quantity while focus is in the product form

   CONSOLE HELPERS
     a11yMenuAudit()     walks every menu and reports anything skipped
     a11yPageAudit()     tab-stop counts and how far the key targets are
     a11yTabOrder()      every tab stop on the page, in order
     a11yAuditImages()   images missing alt text

   VERIFIED ON THE LIVE SITE
     Menus: 8/8 items, 34/34 links, 0 skipped.
     Product page: F4 lands on "הוספה לסל"; ArrowUp twice gives quantity 3,
     announced each time; add to cart announces once and moves focus into the
     drawer; Escape returns focus to the buy button.

   FACTS MEASURED, AND WHAT EACH ONE FORCED

     1. `.list-menu-dropdown.hidden { display: block !important; }`
        The `hidden` class hides nothing. Real hiding is visibility:hidden +
        opacity:0, so a CLOSED submenu still has layout boxes, and .focus()
        on a visibility:hidden element silently does nothing.
        => focusable() checks visibility, not just client rects.

     2. Closed panels are visibility:hidden, so the browser already excludes
        them from the tab order.
        => all tabindex stamping removed; it was interference.

     3. <dropdown-menu> opens itself on focusin — the auditor's complaint #1.
        => handleFocusIn gated to run only when we asked.

     4. <dropdown-menu> keeps hover timers. Moving between menus lets the
        PREVIOUS menu's timer fire a global removeClasses(), wiping the
        animation class off the menu that just opened. Measured: 9/9, 11/11,
        10/10 and 4/4 links unreachable.
        => `.a11y-kb-open` forces visibility independently. Then: 0.

     5. The cart icon is a bare <span id="cart-icon-bubble"> — no href, no
        tabindex, no role.  => promoted to a real tab stop.

     6. Top-level arrow navigation was built from the items that OWN a
        submenu, so the four plain links were never registered.
        => now every top-level item, submenu or not.

     7. THE EVERY-SECOND-ITEM SKIP. The theme binds its keydown listener on
        <dropdown-menu> inside the constructor, so patching the prototype
        changes nothing. One ArrowDown produced THREE focus() calls.
        => arrow keys inside a panel are owned in the CAPTURE phase.

     8. Collection page: 82 tab stops, 39 to reach the first product.
        Product page: 93 tab stops, 27 to reach the buy button.
        => skip links, F2 zone cycling, F4 buy, arrow quantity.

     9. THE CART DRAWER HAS NO CLOSE CONTROL. Measured inside the open
        <cart-drawer>: 1 tab stop total, zero elements matching any close
        selector, zero elements whose class or label mentions close. That is
        the second half of the auditor's item 3.
        => F3 toggles the drawer, Escape closes it, and both restore focus.
        No button is injected into the theme by default; flip
        CONFIG.injectCloseButton to true if you ever want a visible one.

    10. The add was announced TWICE — the fetch wrapper and the form-submit
        listener both caught the same request.
        => a short guard window swallows the duplicate.

    11. Slider controls are labelled in English on a Hebrew store:
        "Previous slide", "Next slide", "View slide image".
        => translated, and image links that duplicate a real link next to
        them are hidden from screen readers instead of being announced.
   ========================================================================== */

(function () {
  'use strict';

  /* ---- kill switch ------------------------------------------------------ */
  try {
    if (new URLSearchParams(location.search).get('a11y') === 'off') {
      console.log('[a11y] disabled via ?a11y=off');
      return;
    }
  } catch (e) {}

  var CONFIG = {
    DEBUG: true,

    /* -- menu ----------------------------------------------------------- */
    topLevel: '.header__inline-menu > ul > li',
    submenu: '.list-menu-dropdown, .menu-drawer__submenu, .mega-menu__content',

    /* -- product cards / cart ------------------------------------------- */
    quickAdd: '.card__quick-add, .quick-add__button, button[name="add"]',
    productCard: '.card, .product-card, .card-wrapper',
    iconButtons: '#cart-icon-bubble, .header__icon--summary',
    cartDrawer: 'cart-drawer, #CartDrawer, .cart-drawer, .drawer--cart',
    cartClose: '.drawer__close, .cart-drawer__close, [data-cart-close], .a11y-cart-close',
    cartKey: 'F3',
    injectCloseButton: false,   // true to also drop a visible × into the panel

    /* -- page landmarks -------------------------------------------------- */
    grid: '#ProductGridContainer',
    filters: 'facet-filters-form, #FacetsWrapperDesktop',
    productInfo: '.product__info-container, .product__info-wrapper',

    /* -- skip links, in order. A missing target drops itself. ------------ */
    skipLinks: [
      { sel: '#ProductGridContainer', text: 'דלג לרשימת המוצרים' },
      { sel: 'facet-filters-form, #FacetsWrapperDesktop', text: 'דלג לסינון' },
      { sel: '.product__info-container, .product__info-wrapper', text: 'דלג לפרטי המוצר' },
      { sel: '#MainContent, main, [role="main"]', text: 'דלג לתוכן הראשי' },
      { sel: 'footer, .footer', text: 'דלג לתחתית העמוד' }
    ],

    /* -- F2 cycles between these; missing ones are skipped --------------- */
    zones: [
      { sel: 'facet-filters-form, #FacetsWrapperDesktop', name: 'סינון' },
      { sel: '#ProductGridContainer', name: 'רשימת המוצרים' },
      { sel: '.product__info-container, .product__info-wrapper', name: 'פרטי המוצר' },
      { sel: '.product-form__buttons, [name="add"]', name: 'רכישה' },
      { sel: '.pagination, [class*="pagination"]', name: 'מעבר בין עמודים' }
    ],
    zoneKey: 'F2',

    /* -- product page ---------------------------------------------------- */
    buyKey: 'F4',
    productForm: '.product form[action*="/cart/add"], #MainContent form[action*="/cart/add"]',
    addToCart: '[name="add"]',
    qtyInput: '.product__info-container .quantity__input, .product-form .quantity__input',
    qtyPlus: '[name="plus"]',
    qtyMinus: '[name="minus"]',

    /* -- slider labels (fact 11) ----------------------------------------- */
    sliderLabels: [
      { match: /^previous slide$/i, text: 'לשקופית הקודמת' },
      { match: /^next slide$/i, text: 'לשקופית הבאה' },
      { match: /^(go to )?slide \d+$/i, text: 'מעבר לשקופית' }
    ],

    /* -- texts ------------------------------------------------------------ */
    addedText: 'המוצר נוסף לעגלה',
    closeCartText: 'סגירת עגלת הקניות',
    cartIconText: 'סל הקניות',
    cartOpened: 'עגלת הקניות נפתחה',
    cartClosed: 'העגלה נסגרה',
    buyAnnounce: 'כפתור הוספה לסל',
    qtyAnnounce: 'כמות ',

    /* -- look & feel ------------------------------------------------------ */
    focusColor: '#000',
    scrollSafety: true,
    hintEnabled: true,
    hintMinWidth: 1024,
    hintHideAfter: 3500
  };

  var FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  var OPEN_CLASS = 'a11y-kb-open';

  var REDUCED = false;
  try { REDUCED = matchMedia('(prefers-reduced-motion: reduce)').matches; } catch (e) {}

  var RTL = getComputedStyle(document.documentElement).direction === 'rtl';
  var KEY_NEXT = RTL ? 'ArrowLeft' : 'ArrowRight';
  var KEY_PREV = RTL ? 'ArrowRight' : 'ArrowLeft';

  var keyboardMode = false;

  function log() {
    if (!CONFIG.DEBUG) return;
    console.log.apply(console, ['[a11y]'].concat([].slice.call(arguments)));
  }

  function safe(name, fn) {
    return function () {
      try { return fn.apply(this, arguments); }
      catch (e) { console.warn('[a11y] ' + name + ' failed:', e); }
    };
  }

  function all(sel, root) {
    try { return [].slice.call((root || document).querySelectorAll(sel)); }
    catch (e) { return []; }
  }

  function visible(el) {
    if (!el) return false;
    var s = getComputedStyle(el);
    if (s.display === 'none' || s.visibility === 'hidden' || s.opacity === '0') return false;
    return el.offsetParent !== null || s.position === 'fixed';
  }

  // Fact 1: geometry alone is not enough on this theme.
  function focusable(el) {
    if (!el || !el.getClientRects().length) return false;
    return getComputedStyle(el).visibility !== 'hidden';
  }

  function scrollTo(el) {
    try { el.scrollIntoView({ block: 'center', behavior: REDUCED ? 'auto' : 'smooth' }); }
    catch (e) { el.scrollIntoView(); }
  }

  function inside(el, sel) {
    if (!el || !el.closest || !sel) return false;
    try { return !!el.closest(sel); } catch (e) { return false; }
  }

  /* ====================================================================== */
  /* LIVE REGION — one announcer shared by cart, zones and quantity         */
  /* ====================================================================== */
  var live = null;
  var lastAnnounce = { text: '', at: 0 };

  function announce(text) {
    if (!live) return;

    // Fact 10: the same add was reported by two different interceptors, so
    // the message was read out twice. Swallow a repeat within a short window.
    var now = Date.now();
    if (text === lastAnnounce.text && now - lastAnnounce.at < 2500) return;
    lastAnnounce = { text: text, at: now };

    // Clearing first is required, or a screen reader stays silent when the
    // same text is announced twice legitimately.
    live.textContent = '';
    setTimeout(function () { live.textContent = text; }, 80);
    setTimeout(function () { live.textContent = ''; }, 4000);
  }

  /* ====================================================================== */
  /* STYLES                                                                 */
  /* ====================================================================== */
  var injectStyles = safe('styles', function () {
    var css = [
      ':focus-visible{outline:3px solid ' + CONFIG.focusColor + ';outline-offset:2px}',
      '.a11y-sr{position:absolute!important;width:1px!important;height:1px!important;overflow:hidden!important;clip:rect(0 0 0 0)!important;white-space:nowrap!important}',

      '.a11y-skip-wrap{position:fixed;top:0;left:0;z-index:2147483647}',
      '.a11y-skip{position:absolute;top:-250px;left:16px;white-space:nowrap;display:inline-block;padding:12px 20px;background:#fff;color:#000;font-size:15px;font-weight:600;text-decoration:none;border:2px solid #000;border-radius:6px;transition:top .15s}',
      '.a11y-skip:focus{top:16px}',
      '.a11y-skip:nth-of-type(2):focus{top:66px}',
      '.a11y-skip:nth-of-type(3):focus{top:116px}',
      '.a11y-skip:nth-of-type(4):focus{top:166px}',
      '.a11y-skip:nth-of-type(5):focus{top:216px}',

      // Only used when CONFIG.injectCloseButton is turned on.
      '.a11y-cart-close{position:absolute;top:12px;inset-inline-end:12px;z-index:5;display:flex;align-items:center;justify-content:center;width:40px;height:40px;padding:0;border:0;border-radius:50%;background:rgba(0,0,0,.06);color:inherit;cursor:pointer;line-height:1;font-size:20px;font-family:inherit;transition:background .15s}',
      '.a11y-cart-close:hover{background:rgba(0,0,0,.12)}',
      '.a11y-cart-close:focus-visible{outline:3px solid ' + CONFIG.focusColor + ';outline-offset:2px}',

      // Fact 4: while keyboard focus owns a submenu it stays visible, no
      // matter what the theme's hover timers do behind our back.
      '.' + OPEN_CLASS + ',' +
      '.' + OPEN_CLASS + ' > .list-menu,' +
      '.' + OPEN_CLASS + ' ul,' +
      '.' + OPEN_CLASS + ' li,' +
      '.' + OPEN_CLASS + ' a{visibility:visible!important;opacity:1!important;pointer-events:auto!important}',

      CONFIG.productCard + ':focus-within ' + CONFIG.quickAdd.split(',')[0] + '{opacity:1!important;visibility:visible!important;pointer-events:auto!important}',

      // Bottom-centre toast. pointer-events:none means it can never block a
      // click, which is what keeps it from feeling intrusive.
      '#a11y-hint{display:none}',
      '@media (min-width:' + CONFIG.hintMinWidth + 'px) and (hover:hover) and (pointer:fine){',
      '#a11y-hint{display:flex;position:fixed;left:50%;bottom:24px;z-index:2147483000;align-items:center;justify-content:center;flex-wrap:wrap;gap:6px;padding:8px 14px;border-radius:999px;background:rgba(20,22,27,.92);-webkit-backdrop-filter:blur(12px);backdrop-filter:blur(12px);border:1px solid rgba(255,255,255,.1);box-shadow:0 10px 32px rgba(0,0,0,.28);color:#fff;font-family:inherit;direction:rtl;max-width:min(94vw,640px);opacity:0;transform:translateX(-50%) translateY(14px);transition:opacity .2s ease,transform .26s cubic-bezier(.2,.9,.3,1.25);pointer-events:none}',
      '#a11y-hint.on{opacity:1;transform:translateX(-50%) translateY(0)}}',
      '.a11y-chip{display:inline-flex;align-items:center;gap:6px;padding:3px 10px 3px 7px;border-radius:999px;background:rgba(255,255,255,.06);font-size:11.5px;line-height:1;white-space:nowrap;transition:background .18s ease,transform .18s ease}',
      '.a11y-chip .cap{display:inline-flex;align-items:center;justify-content:center;min-width:20px;height:20px;padding:0 6px;border-radius:6px;background:rgba(255,255,255,.14);border:1px solid rgba(255,255,255,.22);font-size:10.5px;font-weight:700;font-family:system-ui,-apple-system,sans-serif;direction:ltr}',
      '.a11y-chip .txt2{opacity:.92;font-weight:600}',
      '.a11y-chip.hit{background:#4f7cf7;transform:translateY(-3px)}',
      '.a11y-chip.hit .cap{background:rgba(255,255,255,.3);border-color:rgba(255,255,255,.5)}',
      '@media (prefers-reduced-motion:reduce){#a11y-hint,.a11y-chip{transition:none!important}}'
    ].join('');

    var style = document.createElement('style');
    style.id = 'a11y-styles';
    style.textContent = css;
    document.head.appendChild(style);
  });

  /* ====================================================================== */
  /* SCROLL SAFETY                                                          */
  /* ====================================================================== */
  var cachedOffset = 0, offsetAt = 0;

  function stickyOffset() {
    var now = Date.now();
    if (now - offsetAt < 1000) return cachedOffset;
    offsetAt = now;

    var top = 0;
    all('header, .header, .header-wrapper, .section-header').forEach(function (el) {
      var s = getComputedStyle(el);
      if (s.position !== 'fixed' && s.position !== 'sticky') return;
      var r = el.getBoundingClientRect();
      if (r.top <= 2 && r.height > 0 && r.height < window.innerHeight / 2) {
        top = Math.max(top, r.bottom);
      }
    });
    cachedOffset = top;
    return top;
  }

  var initScrollSafety = safe('scroll', function () {
    if (!CONFIG.scrollSafety) return;

    document.addEventListener('focusin', function (e) {
      if (!keyboardMode) return;          // clicking must never move the page
      var el = e.target;
      if (!el || el === document.body || el === document.documentElement) return;

      try {
        var r = el.getBoundingClientRect();
        if (!r.height) return;
        var safeTop = stickyOffset() + 16;
        var safeBottom = window.innerHeight - 16;
        var delta = 0;

        if (r.top < safeTop) delta = r.top - safeTop;
        else if (r.bottom > safeBottom) delta = r.bottom - safeBottom;
        if (!delta) return;

        window.scrollBy({ top: delta, behavior: REDUCED ? 'auto' : 'smooth' });
      } catch (err) {}
    });
  });

  /* ====================================================================== */
  /* SHARED MENU HELPERS                                                    */
  /* ====================================================================== */
  function topTriggers() {
    var lis = all(CONFIG.topLevel);
    if (!lis.length) lis = all('[data-a11y-menu]').filter(function (li) {
      return !(li.parentElement && li.parentElement.closest('li'));
    });
    return lis.map(function (li) { return li.querySelector('a, button, summary'); })
              .filter(Boolean);
  }

  function closePanel(panel) {
    if (!panel) return;
    panel.classList.remove(OPEN_CLASS);
    var dd = panel.closest('dropdown-menu');
    if (dd) {
      dd.__a11yWantOpen = false;
      if (typeof dd.close === 'function') { try { dd.close(); } catch (e) {} }
    }
    var owner = panel.closest('li');
    var trigger = owner && owner.querySelector('a, button, summary');
    if (trigger) trigger.setAttribute('aria-expanded', 'false');
  }

  // Jump to another top-level menu and open it, from anywhere.
  function gotoTopLevel(from, dir) {
    var list = topTriggers().filter(focusable);
    var i = list.indexOf(from);
    if (i === -1) return false;

    var target = dir > 0 ? (list[i + 1] || list[0])
                         : (list[i - 1] || list[list.length - 1]);
    if (!target) return false;

    target.focus();

    // If it owns a submenu, open it the same way ArrowDown does, so the user
    // lands inside it instead of only on its label.
    var li = target.closest('li');
    if (li && li.querySelector(CONFIG.submenu)) {
      target.dispatchEvent(new KeyboardEvent('keydown', {
        key: 'ArrowDown', bubbles: true, cancelable: true
      }));
    }
    return true;
  }

  /* ====================================================================== */
  /* FACT 7 — OWN THE ARROW KEYS IN THE CAPTURE PHASE                       */
  /* ====================================================================== */
  var ownArrowKeys = safe('arrowKeys', function () {
    if (window.__a11yArrowsOwned) return;
    window.__a11yArrowsOwned = true;

    document.addEventListener('keydown', function (e) {
      var keys = ['ArrowDown', 'ArrowUp', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Escape'];
      if (keys.indexOf(e.key) === -1) return;
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      var active = document.activeElement;
      if (!active || !active.closest) return;

      var panel = active.closest(CONFIG.submenu);
      if (!panel) return;                       // only inside an open submenu

      var owner = panel.closest('li');
      var trigger = owner && owner.querySelector('a, button, summary');

      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopImmediatePropagation();
        closePanel(panel);
        if (trigger) trigger.focus();
        return;
      }

      // Left / Right: jump sideways to the next or previous menu without
      // having to walk back out of this one first.
      if (e.key === KEY_NEXT || e.key === KEY_PREV) {
        if (!trigger) return;
        e.preventDefault();
        e.stopImmediatePropagation();
        closePanel(panel);
        gotoTopLevel(trigger, e.key === KEY_NEXT ? 1 : -1);
        return;
      }

      var items = all(FOCUSABLE, panel).filter(focusable);
      if (!items.length) return;

      var i = items.indexOf(active);
      if (i === -1) {
        for (var n = 0; n < items.length; n++) {
          if (items[n].contains(active)) { i = n; break; }
        }
      }
      if (i === -1) i = 0;

      e.preventDefault();
      e.stopImmediatePropagation();

      var next;
      if (e.key === 'ArrowDown') next = items[i + 1] || items[0];
      else if (e.key === 'ArrowUp') next = items[i - 1] || items[items.length - 1];
      else if (e.key === 'Home') next = items[0];
      else next = items[items.length - 1];

      if (next) next.focus();
    }, true);   // capture — this is the whole point

    log('arrow keys owned in capture phase');
  });

  /* ====================================================================== */
  /* MENUS                                                                  */
  /* ====================================================================== */
  function clearOldStamps(box) {
    all('[data-a11y-ti]', box).forEach(function (el) {
      var prev = el.getAttribute('data-a11y-ti');
      if (prev === '') el.removeAttribute('tabindex');
      else el.setAttribute('tabindex', prev);
      el.removeAttribute('data-a11y-ti');
    });
  }

  var gateDropdownMenu = safe('gate', function () {
    var DD = window.customElements && customElements.get('dropdown-menu');
    if (!DD || DD.prototype.__a11yGated) return;
    DD.prototype.__a11yGated = true;

    var orig = DD.prototype.handleFocusIn;
    if (typeof orig !== 'function') return;

    DD.prototype.handleFocusIn = function (e) {
      if (this.__a11yWantOpen) return orig.call(this, e);
    };
    log('dropdown-menu focus-open gated');
  });

  function closeAllKeyboardMenus(except) {
    all('.' + OPEN_CLASS).forEach(function (el) {
      if (el !== except) el.classList.remove(OPEN_CLASS);
    });
  }

  var initMenus = safe('menus', function () {
    gateDropdownMenu();
    ownArrowKeys();
    clearOldStamps(document);

    var parents = all('nav li, header li, .header li, .menu-drawer li').filter(function (li) {
      var sub = li.querySelector(CONFIG.submenu);
      return sub && sub.closest('li') === li;
    });

    log('submenu owners:', parents.length);

    parents.forEach(function (li) {
      if (li.hasAttribute('data-a11y-menu')) return;
      li.setAttribute('data-a11y-menu', '1');

      var sub = li.querySelector(CONFIG.submenu);
      var trigger = li.querySelector('a, button, summary');
      if (!sub || !trigger) return;

      var dd = trigger.closest('dropdown-menu');
      var details = li.querySelector('details') || li.closest('details');

      trigger.setAttribute('aria-haspopup', 'true');
      trigger.setAttribute('aria-expanded', 'false');

      function open(e) {
        // Fact 4. Our own class first: it is the only thing that survives the
        // theme's hover timers wiping classes off a menu we just opened.
        closeAllKeyboardMenus(sub);
        sub.classList.add(OPEN_CLASS);

        if (dd) {
          dd.__a11yWantOpen = true;
          if (typeof dd.handleFocusIn === 'function') dd.handleFocusIn(e);
        } else if (details && !details.open) {
          details.open = true;
        }

        trigger.setAttribute('aria-expanded', 'true');
      }

      trigger.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowDown') return;
        e.preventDefault();
        open(e);

        // setTimeout, not requestAnimationFrame: rAF is paused in a tab that
        // is not in the foreground, and then focus never moves at all.
        var tries = 0;
        (function tryFocus() {
          var first = all(FOCUSABLE, sub).filter(focusable)[0];
          if (first) { first.focus(); return; }
          if (++tries < 25) setTimeout(tryFocus, 20);
        })();
      });

      li.addEventListener('focusout', function (e) {
        if (li.contains(e.relatedTarget)) return;
        if (sub.classList.contains(OPEN_CLASS)) closePanel(sub);
      });

      li.addEventListener('mouseenter', function () {
        trigger.setAttribute('aria-expanded', 'true');
      });
      li.addEventListener('mouseleave', function () {
        setTimeout(function () {
          if (!sub.classList.contains(OPEN_CLASS) && !visible(sub)) {
            trigger.setAttribute('aria-expanded', 'false');
          }
        }, 80);
      });
    });

    /* -- left/right across EVERY top-level item --------------------------- */
    var triggers = topTriggers();
    log('top-level items with arrow navigation:', triggers.length);

    triggers.forEach(function (trigger) {
      if (trigger.hasAttribute('data-a11y-h')) return;
      trigger.setAttribute('data-a11y-h', '1');

      trigger.addEventListener('keydown', function (e) {
        if (e.key !== KEY_NEXT && e.key !== KEY_PREV) return;
        e.preventDefault();
        gotoTopLevel(trigger, e.key === KEY_NEXT ? 1 : -1);
      });
    });
  });

  /* ====================================================================== */
  /* ZONE JUMP — F2 cycles the page's working areas                         */
  /* ====================================================================== */
  var initZoneJump = safe('zoneJump', function () {
    function zones() {
      return CONFIG.zones
        .map(function (z) {
          var el = document.querySelector(z.sel);
          return (el && visible(el)) ? { el: el, name: z.name } : null;
        })
        .filter(Boolean);
    }

    document.addEventListener('keydown', function (e) {
      if (e.key !== CONFIG.zoneKey || e.altKey || e.ctrlKey || e.metaKey) return;

      var list = zones();
      if (list.length < 2) return;

      e.preventDefault();

      var active = document.activeElement;
      var here = -1;
      for (var n = 0; n < list.length; n++) {
        if (list[n].el.contains(active)) { here = n; break; }
      }

      var dir = e.shiftKey ? -1 : 1;
      var target = (here === -1) ? list[0]
                                 : list[(here + dir + list.length) % list.length];

      var first = all(FOCUSABLE, target.el).filter(focusable)[0];
      var dest = first || target.el;
      if (!first && !dest.hasAttribute('tabindex')) dest.setAttribute('tabindex', '-1');

      keyboardMode = true;
      scrollTo(dest);
      dest.focus();
      announce(target.name);
    }, true);

    log('zone jump ready on ' + CONFIG.zoneKey);
  });

  /* ====================================================================== */
  /* PRODUCT PAGE KEYS — F4 to buy, arrows for quantity                     */
  /* ====================================================================== */
  var initProductKeys = safe('productKeys', function () {
    var form = document.querySelector(CONFIG.productForm);
    if (!form) return;

    var atc = document.querySelector(CONFIG.addToCart);
    var qty = document.querySelector(CONFIG.qtyInput);

    function bump(dir) {
      if (!qty) return false;
      var btn = form.querySelector(dir > 0 ? CONFIG.qtyPlus : CONFIG.qtyMinus);
      if (btn) {
        btn.click();                 // use the theme's own stepper if present
      } else {
        var step = parseFloat(qty.step) || 1;
        var min = (qty.min === '' ? 1 : parseFloat(qty.min));
        var next = (parseFloat(qty.value) || 1) + dir * step;
        if (!isNaN(min) && next < Math.max(min, 1)) next = Math.max(min, 1);
        qty.value = next;
        qty.dispatchEvent(new Event('change', { bubbles: true }));
      }
      announce(CONFIG.qtyAnnounce + qty.value);
      return true;
    }

    document.addEventListener('keydown', function (e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;

      if (e.key === CONFIG.buyKey && atc) {
        e.preventDefault();
        keyboardMode = true;
        scrollTo(atc);
        atc.focus();
        announce(CONFIG.buyAnnounce);
        return;
      }

      var active = document.activeElement;
      if (!active || !form.contains(active)) return;
      if (active === qty) return;
      if (/^(text|search|email|tel|url)$/i.test(active.type || '')) return;

      var up = (e.key === 'ArrowUp' || e.key === KEY_NEXT);
      var down = (e.key === 'ArrowDown' || e.key === KEY_PREV);
      if (!up && !down) return;

      if (bump(up ? 1 : -1)) {
        e.preventDefault();
        e.stopImmediatePropagation();
      }
    }, true);

    log('product keys ready — ' + CONFIG.buyKey + ' to buy, arrows for quantity');
  });

  /* ====================================================================== */
  /* QUICK ADD                                                              */
  /* ====================================================================== */
  var initQuickAdd = safe('quickAdd', function () {
    var buttons = all(CONFIG.quickAdd);
    log('quick-add controls:', buttons.length);

    buttons.forEach(function (el) {
      if (el.hasAttribute('data-a11y-qa')) return;
      el.setAttribute('data-a11y-qa', '1');

      if (el.getAttribute('tabindex') === '-1') el.removeAttribute('tabindex');

      var tag = el.tagName.toLowerCase();
      var isButton = tag === 'button' || (tag === 'input' && /submit|button/i.test(el.type));
      var isLink = tag === 'a' && el.hasAttribute('href');

      if (!isButton && !isLink) {
        if (!el.hasAttribute('role')) el.setAttribute('role', 'button');
        if (!el.hasAttribute('tabindex')) el.setAttribute('tabindex', '0');
        el.addEventListener('keydown', function (e) {
          if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.click(); }
        });
      }
    });
  });

  /* ====================================================================== */
  /* ICON BUTTONS                                                           */
  /* Fact 5: the cart icon is a bare <span> — no href, no tabindex, no role. */
  /* ====================================================================== */
  var initIconButtons = safe('iconButtons', function () {
    all(CONFIG.iconButtons).forEach(function (el) {
      if (el.hasAttribute('data-a11y-icon')) return;
      if (el.tagName === 'A' || el.tagName === 'BUTTON') return;
      if (el.querySelector('a[href], button')) return;   // already reachable inside

      el.setAttribute('data-a11y-icon', '1');
      el.setAttribute('role', 'button');
      el.setAttribute('tabindex', '0');

      if (!el.getAttribute('aria-label') && !el.querySelector('.visually-hidden')) {
        el.setAttribute('aria-label', CONFIG.cartIconText);
      }

      el.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); el.click(); }
      });

      log('icon made reachable:', el.id || el.className);
    });
  });

  /* ====================================================================== */
  /* CART                                                                   */
  /* ====================================================================== */
  var cartTrigger = null, openDrawer = null;

  // Off by default. Fact 9 is solved with the F3 key instead, so nothing is
  // injected into the theme's own markup unless you ask for it.
  function ensureCloseButton(drawer) {
    var existing = drawer.querySelector(CONFIG.cartClose);
    if (!CONFIG.injectCloseButton) return existing;
    if (existing) return existing;

    var btn = document.createElement('button');
    btn.type = 'button';
    btn.className = 'a11y-cart-close';
    btn.setAttribute('aria-label', CONFIG.closeCartText);
    btn.innerHTML = '<span aria-hidden="true">&times;</span>';
    btn.addEventListener('click', function () { closeCartDrawer(drawer); });

    var host = drawer.querySelector('.drawer__inner, .cart-drawer__inner, .drawer__header') || drawer;
    if (getComputedStyle(host).position === 'static') host.style.position = 'relative';
    host.insertBefore(btn, host.firstChild);

    log('cart close button injected');
    return btn;
  }

  // Close by every means the theme might understand, then fall back to
  // clicking the cart icon again, which is what toggles it here.
  function closeCartDrawer(drawer) {
    var done = false;
    try {
      if (typeof drawer.close === 'function') { drawer.close(); done = true; }
    } catch (e) {}

    if (!done) {
      ['is-open', 'active', 'open', 'animate'].forEach(function (c) { drawer.classList.remove(c); });
      drawer.removeAttribute('open');
      document.documentElement.classList.remove('overflow-hidden');
      document.body.classList.remove('overflow-hidden');
    }

    setTimeout(function () {
      if (visible(drawer)) {
        var icon = document.querySelector('#cart-icon-bubble');
        if (icon) icon.click();
      }
      endTrap();
    }, 60);
  }

  function enterDrawer(drawer, silent) {
    openDrawer = drawer;
    document.addEventListener('keydown', trapKeys, true);
    ensureCloseButton(drawer);

    var first = all(FOCUSABLE, drawer).filter(focusable)[0];
    if (first) first.focus();
    if (!silent) announce(CONFIG.cartOpened);

    var watch = setInterval(function () {
      if (!document.body.contains(drawer) || !visible(drawer)) {
        clearInterval(watch);
        endTrap();
      }
    }, 400);
  }

  function trapKeys(e) {
    if (!openDrawer) return;

    if (e.key === 'Escape') {
      e.preventDefault();
      closeCartDrawer(openDrawer);
      announce(CONFIG.cartClosed);
      return;
    }
    if (e.key !== 'Tab') return;

    var items = all(FOCUSABLE, openDrawer).filter(focusable);
    if (!items.length) return;
    var first = items[0], last = items[items.length - 1];

    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function endTrap() {
    if (!openDrawer) return;
    document.removeEventListener('keydown', trapKeys, true);
    openDrawer = null;
    if (cartTrigger && document.body.contains(cartTrigger)) {
      var t = cartTrigger;
      setTimeout(function () { t.focus(); }, 50);
    }
    cartTrigger = null;
  }

  var cartAdded = safe('cartAdded', function (trigger) {
    announce(CONFIG.addedText);
    cartTrigger = (trigger && trigger !== document.body) ? trigger : null;
    log('cart add detected');

    var stop = Date.now() + 3000;
    var poll = setInterval(function () {
      var drawer = all(CONFIG.cartDrawer).filter(visible)[0];
      if (drawer) {
        clearInterval(poll);
        enterDrawer(drawer, true);   // the add was already announced
      } else if (Date.now() > stop) {
        clearInterval(poll);
        log('no cart drawer appeared');
      }
    }, 150);
  });

  // Fact 9: the drawer has no close control of its own. F3 is the toggle —
  // it opens the cart and steps into it, and closes it and hands focus back.
  var initCartKey = safe('cartKey', function () {
    document.addEventListener('keydown', function (e) {
      if (e.key !== CONFIG.cartKey || e.altKey || e.ctrlKey || e.metaKey) return;
      e.preventDefault();

      var drawer = all(CONFIG.cartDrawer).filter(visible)[0];

      if (drawer) {                       // open -> close
        closeCartDrawer(drawer);
        announce(CONFIG.cartClosed);
        return;
      }

      var icon = document.querySelector('#cart-icon-bubble');
      if (!icon) return;

      keyboardMode = true;
      cartTrigger = (document.activeElement && document.activeElement !== document.body)
        ? document.activeElement : null;
      icon.click();

      var stop = Date.now() + 2500;
      (function waitForDrawer() {
        var d = all(CONFIG.cartDrawer).filter(visible)[0];
        if (d) { enterDrawer(d); return; }
        if (Date.now() < stop) setTimeout(waitForDrawer, 60);
      })();
    }, true);

    log('cart toggle ready on ' + CONFIG.cartKey);
  });

  var initCart = safe('cart', function () {
    live = document.createElement('div');
    live.className = 'a11y-sr';
    live.setAttribute('role', 'status');
    live.setAttribute('aria-live', 'polite');
    document.body.appendChild(live);

    var isAdd = function (u) { return /\/cart\/add/.test(String(u || '')); };

    var origFetch = window.fetch;
    if (origFetch) {
      window.fetch = function () {
        var args = [].slice.call(arguments);
        var watching = false, trigger = null;
        try {
          var url = (args[0] && args[0].url) ? args[0].url : args[0];
          watching = isAdd(url);
          if (watching) trigger = document.activeElement;
        } catch (e) {}

        return origFetch.apply(this, args).then(function (r) {
          if (watching && r && r.ok) cartAdded(trigger);
          return r;
        });
      };
    }

    var xo = XMLHttpRequest.prototype.open;
    var xs = XMLHttpRequest.prototype.send;
    XMLHttpRequest.prototype.open = function (m, u) { this._a11yUrl = u; return xo.apply(this, arguments); };
    XMLHttpRequest.prototype.send = function () {
      try {
        if (isAdd(this._a11yUrl)) {
          var trigger = document.activeElement, x = this;
          x.addEventListener('load', function () {
            if (x.status >= 200 && x.status < 300) cartAdded(trigger);
          });
        }
      } catch (e) {}
      return xs.apply(this, arguments);
    };

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (form && /\/cart\/add/.test(form.getAttribute('action') || '')) {
        cartAdded(document.activeElement);
      }
    }, true);

    // Opening the drawer by clicking the icon should get the same treatment.
    document.addEventListener('click', function (e) {
      if (!e.target.closest || !e.target.closest(CONFIG.iconButtons)) return;
      setTimeout(function () {
        var drawer = all(CONFIG.cartDrawer).filter(visible)[0];
        if (drawer && !openDrawer) ensureCloseButton(drawer);
      }, 350);
    }, true);
  });

  /* ====================================================================== */
  /* SLIDER LABELS                                                          */
  /* Fact 11: English labels on a Hebrew store, "View slide" overriding real */
  /* button text, and "View slide image" on links with no text at all.       */
  /* ====================================================================== */
  var initSliderLabels = safe('sliderLabels', function () {
    var fixed = 0, translated = 0, hidden = 0;

    all('[aria-label]').forEach(function (el) {
      if (el.hasAttribute('data-a11y-lbl')) return;
      var label = (el.getAttribute('aria-label') || '').trim();
      if (!label) return;

      var text = el.textContent.trim();

      // A bogus label on an element that HAS real text: drop the label so the
      // text is announced instead.
      if (/^(view|go to|goto)?\s*slide\b|^slide\s*\d*$/i.test(label) && text) {
        el.setAttribute('data-a11y-lbl', label);
        el.removeAttribute('aria-label');
        fixed++;
        return;
      }

      // An English control label: translate it.
      for (var i = 0; i < CONFIG.sliderLabels.length; i++) {
        if (CONFIG.sliderLabels[i].match.test(label)) {
          el.setAttribute('data-a11y-lbl', label);
          el.setAttribute('aria-label', CONFIG.sliderLabels[i].text);
          translated++;
          return;
        }
      }

      // "View slide image" on a link with no text: if a sibling link already
      // points at the same place, this one is decoration.
      if (/view slide image/i.test(label) && !text && el.tagName === 'A') {
        var href = el.getAttribute('href');
        var twin = href && all('a[href="' + href + '"]').filter(function (a) {
          return a !== el && a.textContent.trim();
        })[0];
        el.setAttribute('data-a11y-lbl', label);
        if (twin) {
          el.setAttribute('aria-hidden', 'true');
          el.setAttribute('tabindex', '-1');
          hidden++;
        } else {
          el.setAttribute('aria-label', 'מעבר לקולקציה');
          translated++;
        }
      }
    });

    if (fixed || translated || hidden) {
      log('slider labels — cleared:', fixed, 'translated:', translated, 'hidden as duplicates:', hidden);
    }
  });

  /* ====================================================================== */
  /* SKIP LINKS — the first tab stops on the page                           */
  /* ====================================================================== */
  var initSkipLinks = safe('skipLinks', function () {
    var wrap = document.createElement('div');
    wrap.className = 'a11y-skip-wrap';
    var made = 0;

    CONFIG.skipLinks.forEach(function (item) {
      var target = document.querySelector(item.sel);
      if (!target) return;                      // not on this page — drop it
      if (!target.id) target.id = 'a11y-target-' + (made + 1);
      if (!target.hasAttribute('tabindex')) target.setAttribute('tabindex', '-1');

      var link = document.createElement('a');
      link.className = 'a11y-skip';
      link.href = '#' + target.id;
      link.textContent = item.text;
      link.addEventListener('click', function (e) {
        e.preventDefault();
        keyboardMode = true;
        try { target.scrollIntoView({ block: 'start', behavior: REDUCED ? 'auto' : 'smooth' }); }
        catch (err) { target.scrollIntoView(); }
        setTimeout(function () { target.focus(); }, 0);
      });
      wrap.appendChild(link);
      made++;
    });

    if (!made) { log('skip links: no targets on this page'); return; }
    document.body.insertBefore(wrap, document.body.firstChild);
    log('skip links ready:', made);
  });

  /* ====================================================================== */
  /* HINT TOAST                                                             */
  /* ====================================================================== */
  function chipsForContext() {
    var a = document.activeElement;

    var inPanel = inside(a, CONFIG.submenu);
    var inDrawer = inside(a, CONFIG.cartDrawer);
    var onTopItem = !!(a && a.hasAttribute && a.hasAttribute('data-a11y-h'));
    var form = document.querySelector(CONFIG.productForm);
    var inForm = !!(form && a && form.contains(a));
    var onProduct = !!form;
    var grid = document.querySelector(CONFIG.grid);
    var filters = document.querySelector(CONFIG.filters);
    var inGrid = !!(grid && a && grid.contains(a));
    var inFilters = !!(filters && a && filters.contains(a));

    if (inDrawer) return [
      { id: 'tab',   cap: 'Tab',   txt: 'בתוך העגלה' },
      { id: 'enter', cap: 'Enter', txt: 'הפעלה' },
      { id: 'cart',  cap: 'F3',    txt: 'סגירת העגלה' },
      { id: 'esc',   cap: 'Esc',   txt: 'סגירה' }
    ];

    if (inPanel) return [
      { id: 'move',  cap: '↑ ↓',   txt: 'ניווט בתפריט' },
      { id: 'side',  cap: '← →',   txt: 'לתפריט הבא' },
      { id: 'enter', cap: 'Enter', txt: 'מעבר לעמוד' },
      { id: 'esc',   cap: 'Esc',   txt: 'יציאה' }
    ];

    if (onTopItem) return [
      { id: 'arrow', cap: '↓',     txt: 'פתיחת תפריט' },
      { id: 'side',  cap: '← →',   txt: 'בין תפריטים' },
      { id: 'tab',   cap: 'Tab',   txt: 'לפריט הבא' }
    ];

    if (inForm) return [
      { id: 'qty',   cap: '↑ ↓',   txt: 'שינוי כמות' },
      { id: 'enter', cap: 'Enter', txt: 'הוספה לסל' },
      { id: 'cart',  cap: 'F3',    txt: 'פתיחת העגלה' },
      { id: 'zone',  cap: 'F2',    txt: 'בין אזורי העמוד' }
    ];

    if (onProduct) return [
      { id: 'buy',   cap: 'F4',    txt: 'לרכישה מהירה' },
      { id: 'cart',  cap: 'F3',    txt: 'פתיחת העגלה' },
      { id: 'zone',  cap: 'F2',    txt: 'בין אזורי העמוד' },
      { id: 'tab',   cap: 'Tab',   txt: 'לפריט הבא' }
    ];

    if (inFilters) return [
      { id: 'zone',  cap: 'F2',    txt: 'למוצרים' },
      { id: 'enter', cap: 'Enter', txt: 'סימון פילטר' },
      { id: 'tab',   cap: 'Tab',   txt: 'לפילטר הבא' }
    ];

    if (inGrid) return [
      { id: 'zone',  cap: 'F2',    txt: 'חזרה לסינון' },
      { id: 'enter', cap: 'Enter', txt: 'מעבר למוצר' },
      { id: 'cart',  cap: 'F3',    txt: 'פתיחת העגלה' },
      { id: 'tab',   cap: 'Tab',   txt: 'למוצר הבא' }
    ];

    if (grid || filters) return [
      { id: 'zone',  cap: 'F2',    txt: 'סינון ↔ מוצרים' },
      { id: 'cart',  cap: 'F3',    txt: 'פתיחת העגלה' },
      { id: 'move',  cap: '← →',   txt: 'בין תפריטים' },
      { id: 'tab',   cap: 'Tab',   txt: 'לפריט הבא' }
    ];

    return [
      { id: 'tab',   cap: 'Tab',       txt: 'לפריט הבא' },
      { id: 'back',  cap: 'Shift+Tab', txt: 'לפריט הקודם' },
      { id: 'move',  cap: '← →',       txt: 'בין תפריטים' },
      { id: 'cart',  cap: 'F3',        txt: 'פתיחת העגלה' }
    ];
  }

  function chipForKey(e) {
    var a = document.activeElement;
    var inPanel = inside(a, CONFIG.submenu);
    var form = document.querySelector(CONFIG.productForm);
    var inForm = !!(form && a && form.contains(a));

    if (e.key === 'Tab') return e.shiftKey ? 'back' : 'tab';
    if (e.key === CONFIG.zoneKey) return 'zone';
    if (e.key === CONFIG.cartKey) return 'cart';
    if (e.key === CONFIG.buyKey) return 'buy';
    if (e.key === 'Enter') return 'enter';
    if (e.key === 'Escape') return 'esc';
    if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
      return inForm ? 'qty' : (inPanel ? 'side' : 'move');
    }
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      return inForm ? 'qty' : (inPanel ? 'move' : 'arrow');
    }
    return null;
  }

  var initHint = safe('hint', function () {
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Tab' || e.key.indexOf('Arrow') === 0) keyboardMode = true;
    }, true);
    document.addEventListener('mousedown', function () { keyboardMode = false; }, true);

    if (!CONFIG.hintEnabled) return;

    var panel = document.createElement('div');
    panel.id = 'a11y-hint';
    panel.setAttribute('aria-hidden', 'true');   // sighted keyboard users only
    panel.setAttribute('role', 'presentation');
    document.body.appendChild(panel);

    var hideTimer = null;
    var lastSignature = '';

    function wide() {
      try {
        return matchMedia('(min-width:' + CONFIG.hintMinWidth + 'px) and (hover:hover) and (pointer:fine)').matches;
      } catch (e) { return false; }
    }

    function render(hitId) {
      var chips = chipsForContext();
      var signature = chips.map(function (c) { return c.id; }).join(',');

      // Only rebuild the DOM when the context actually changed; otherwise
      // just move the highlight, so nothing flickers while you hold Tab.
      if (signature !== lastSignature) {
        lastSignature = signature;
        panel.innerHTML = chips.map(function (c) {
          return '<span class="a11y-chip" data-id="' + c.id + '">' +
                   '<span class="cap">' + c.cap + '</span>' +
                   '<span class="txt2">' + c.txt + '</span>' +
                 '</span>';
        }).join('');
      }

      all('.a11y-chip', panel).forEach(function (chip) {
        chip.classList.toggle('hit', !!hitId && chip.getAttribute('data-id') === hitId);
      });
    }

    function show(hitId) {
      if (!wide()) return;
      render(hitId);
      panel.classList.add('on');
      clearTimeout(hideTimer);
      hideTimer = setTimeout(hide, CONFIG.hintHideAfter);
    }

    function hide() {
      clearTimeout(hideTimer);
      panel.classList.remove('on');
      all('.a11y-chip', panel).forEach(function (c) { c.classList.remove('hit'); });
    }

    document.addEventListener('keydown', function (e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      var hit = chipForKey(e);
      if (!hit) return;
      show(hit);
    }, true);

    document.addEventListener('focusin', function () {
      if (panel.classList.contains('on')) render(null);
    });

    document.addEventListener('mousedown', hide, true);

    log('hint toast ready');
  });

  /* ====================================================================== */
  /* CONSOLE HELPERS                                                        */
  /* ====================================================================== */
  window.a11yTabOrder = function () {
    var els = all(FOCUSABLE).filter(focusable).filter(function (el) { return el.tabIndex >= 0; });
    console.table(els.map(function (el, i) {
      return {
        i: i,
        tag: el.tagName,
        text: (el.textContent || '').trim().slice(0, 45) || el.getAttribute('aria-label') || '(no text)',
        cls: String(el.className || '').slice(0, 40)
      };
    }));
    return els;
  };

  window.a11yPageAudit = function () {
    var stops = all(FOCUSABLE).filter(focusable).filter(function (el) { return el.tabIndex >= 0; });
    var where = function (sel) {
      var el = document.querySelector(sel);
      if (!el) return null;
      for (var i = 0; i < stops.length; i++) if (el === stops[i] || el.contains(stops[i])) return i;
      return -1;
    };
    var rows = [
      { target: 'סינון', tabs: where(CONFIG.filters) },
      { target: 'רשימת המוצרים', tabs: where(CONFIG.grid) },
      { target: 'פרטי המוצר', tabs: where(CONFIG.productInfo) },
      { target: 'הוספה לסל', tabs: where(CONFIG.addToCart) },
      { target: 'פוטר', tabs: where('footer, .footer') }
    ].filter(function (r) { return r.tabs !== null; });

    console.log('total tab stops on this page: ' + stops.length);
    console.table(rows);
    console.log('With the skip links and F2 / F3 / F4, each of these is 1–2 keys instead.');
    return rows;
  };

  window.a11yMenuAudit = function () {
    var lis = all(CONFIG.topLevel);
    var rows = [], i = 0;

    (function step() {
      if (i >= lis.length) {
        console.table(rows);
        var bad = rows.filter(function (r) { return !r.arrowNav || r.missed > 0; });
        console.log(bad.length === 0
          ? 'All good — every item reachable, nothing skipped.'
          : bad.length + ' menus have problems (see rows above).');
        return;
      }

      var li = lis[i];
      var trigger = li.querySelector('a, button, summary');
      var sub = li.querySelector(CONFIG.submenu);
      if (!trigger) { i++; return step(); }

      trigger.focus();
      trigger.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true, cancelable: true }));

      setTimeout(function () {
        var links = sub ? all('a[href]', sub).filter(focusable) : [];
        var seen = {};
        for (var n = 0; n < links.length; n++) {
          var el = document.activeElement;
          var at = links.indexOf(el);
          if (at !== -1) seen[at] = true;
          el.dispatchEvent(new KeyboardEvent('keydown', { key: 'ArrowDown', bubbles: true, cancelable: true }));
        }
        var missed = links.filter(function (l, n) { return !seen[n]; });

        rows.push({
          menu: trigger.textContent.trim().slice(0, 24),
          arrowNav: trigger.hasAttribute('data-a11y-h'),
          links: links.length,
          visited: Object.keys(seen).length,
          missed: missed.length,
          firstMissed: missed.length ? missed[0].textContent.trim().slice(0, 18) : ''
        });
        i++;
        step();
      }, 450);
    })();

    return 'auditing ' + lis.length + ' top-level items…';
  };

  window.a11yAuditImages = function () {
    var rows = [];
    all('img').forEach(function (img) {
      var alt = img.getAttribute('alt');
      var file = (img.currentSrc || img.src || '').split('?')[0].split('/').pop();
      var problem = null;
      if (alt === null) problem = 'no alt attribute';
      else if (!alt.trim()) problem = 'empty alt (fine only if decorative)';
      if (problem) rows.push({ problem: problem, file: file, width: img.naturalWidth });
    });
    console.table(rows);
    console.log('Fix these in Shopify admin > Content > Files.');
    return rows;
  };

  /* ====================================================================== */
  /* BOOT                                                                   */
  /* ====================================================================== */
  var scanning = false;

  function rescan() {
    if (scanning) return;
    scanning = true;
    try {
      initMenus();
      initQuickAdd();
      initIconButtons();
      initSliderLabels();
    } finally {
      scanning = false;
    }
  }

  var start = safe('start', function () {
    injectStyles();
    initCart();              // creates the live region the others announce through
    initCartKey();
    initSkipLinks();
    initScrollSafety();
    initZoneJump();
    initProductKeys();
    initHint();
    rescan();

    // childList only, never attributes. Watching attributes froze an earlier
    // version, since this script writes attributes itself.
    var pending;
    new MutationObserver(function () {
      clearTimeout(pending);
      pending = setTimeout(rescan, 400);
    }).observe(document.body, { childList: true, subtree: true });

    document.addEventListener('shopify:section:load', function () { setTimeout(rescan, 50); });

    log('ready — a11yMenuAudit() and a11yPageAudit() check the result');
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();